import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import TrajectoryLedger from './TrajectoryLedger';
import TrajectoryInspector from './TrajectoryInspector';

const PAGE = 300;

export default function TrajectoryView({ sessionId, pollMs = 8000 }) {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMoreBefore, setHasMoreBefore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);

  // Same generation guard as the chat: a poll that resolves after a newer one
  // must not overwrite fresher state.
  const genRef = useRef(0);

  const loadTail = useCallback(async () => {
    const gen = ++genRef.current;
    try {
      const view = await api.getTrajectory(sessionId, { limit: PAGE });
      if (gen !== genRef.current) return;
      setRecords(prev => {
        // Keep an already-loaded older prefix: refreshing the tail must not
        // throw away pages the user explicitly asked for.
        const keep = prev.filter(r => r.seq < (view.records[0]?.seq ?? 0));
        return [...keep, ...view.records];
      });
      setTotal(view.total);
      setHasMoreBefore(view.has_more_before);
      setError(null);
    } catch (e) {
      if (gen === genRef.current) setError(e);
    } finally {
      if (gen === genRef.current) setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadTail();
    const timer = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      loadTail();
    }, pollMs);
    return () => { clearInterval(timer); ++genRef.current; };
  }, [loadTail, pollMs]);

  const loadOlder = async () => {
    if (records.length === 0) return;
    setLoadingOlder(true);
    try {
      const view = await api.getTrajectory(sessionId, {
        limit: PAGE, before_seq: records[0].seq,
      });
      setRecords(prev => [...view.records, ...prev]);
      setTotal(view.total);
      setHasMoreBefore(view.has_more_before);
    } catch (e) {
      setError(e);
    } finally {
      setLoadingOlder(false);
    }
  };

  if (loading) return <div className="loading">Загрузка траектории…</div>;

  return (
    <div className="traj-root">
      {error && (
        <div className="chat-stale-banner">
          Обновление траектории не удалось ({error.message}).
        </div>
      )}
      <div className="traj-panes">
        <TrajectoryLedger
          records={records}
          total={total}
          hasMoreBefore={hasMoreBefore}
          selectedId={selected?.id}
          onSelect={rec => setSelected(cur => (cur?.id === rec.id ? null : rec))}
          onLoadOlder={loadOlder}
          loadingOlder={loadingOlder}
        />
        <TrajectoryInspector rec={selected} onClose={() => setSelected(null)} />
      </div>
    </div>
  );
}
